<template>
  <div class="spin-history">
    <div class="all-filters">
      <div class="filters">
        <div class="filters__container">
          <UniversalSelect
            v-model="filters.sector_type"
            style="width: 180px;"
            :options="sectorTypeOptions"
            placeholder="Sector type"
            title="Sector type"
            :disabled="loading || !hasListData"
            @change="changeFilters"
          />
          <div class="d-flex flex-column">
            <span class="font-weight-bold">Start date</span>
            <a-date-picker
              v-model="filters.startDate"
              format="YYYY-MM-DD HH:mm"
              :show-time="{
                defaultValue: $moment('00:00:00', 'HH:mm'),
                format: 'HH:mm'
              }"
              type="date"
              size="large"
              placeholder="Start date"
              :disabled="loading || !hasListData"
              @change="changeFilters"
            />
          </div>
          <div class="d-flex flex-column">
            <span class="font-weight-bold">End date</span>
            <a-date-picker
              v-model="filters.endDate"
              format="YYYY-MM-DD HH:mm"
              :show-time="{
                defaultValue: $moment('00:00:00', 'HH:mm'),
                format: 'HH:mm'
              }"
              type="date"
              size="large"
              placeholder="End date"
              :disabled="loading || !hasListData"
              @change="changeFilters"
            />
          </div>
          <UniversalSelect
            v-model="filters.transaction_status"
            style="width: 180px;"
            :options="statusOptions"
            placeholder="Select status"
            title="Status"
            :disabled="loading || !hasListData"
            @change="changeFilters"
          />
          <div class="d-flex align-items-end" style="height: 62px;">
            <SearchField
              :value="filters.search"
              ref="search-field"
              :loading="loading"
              :disabled="loading || !hasListData"
              @search="changeSearchValue"
            />
          </div>
        </div>
      </div>
      <a-button
        v-if="isFiltered"
        size="large"
        :disabled="loading"
        class="w-fit"
        @click="() => handleReset()"
      >
        Reset filters
      </a-button>
    </div>
    <a-table
      :dataSource="data"
      :pagination="meta"
      :columns="tableColumns"
      :row-key="(record) => record.id"
      :loading="loading"
      :customRow="customRow"
      @change="changePagination"
    >
      <template slot="created_at" slot-scope="created_at">
        <span>{{ created_at ? format(created_at) : '-' }}</span>
      </template>
      <template slot="login_id" slot-scope="login_id">
        <CopyValue
          :value="login_id"
          message="Login ID copied to clipboard"
        />
      </template>
      <template slot="install_id" slot-scope="install_id">
        <CopyValue
          :value="install_id"
          message="Install ID copied to clipboard"
        />
      </template>
      <template slot="sector_type" slot-scope="sector_type">
        {{ getTypeName(sector_type) }}
      </template>
      <template slot="value" slot-scope="value">
        <CopyValue
          v-if="value"
          :value="value"
          message="Value copied to clipboard"
        />
        <span v-else>—</span>
      </template>
      <template slot="transaction_status" slot-scope="transaction_status, record">
        <a-tooltip placement="topLeft" arrow-point-at-center :getPopupContainer="getPopupContainer">
          <template v-if="transaction_status === 'failed' && record.transaction_error" slot="title">
            <span>{{ record.transaction_error }}</span>
          </template>
          <a-badge
            :count="transaction_status"
            :number-style="getBadgeStatusStyle(transaction_status)"
            style="transform: scale(1.2);"
          />
        </a-tooltip>
      </template>
    </a-table>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import CopyValue from '@/components/custom/form/CopyValue.vue'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { getSectorTypeName } from '@/views/apps/fortune-wheel/composables/useGetSectorTypeName.js'
import { getSectorTypeOptions } from '@/views/apps/fortune-wheel/composables/useGetSectorTypeOptions'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { copyToClipboard } from '@/composables/copy/copyToClipboard.js'
import { UTCFormatInCurrentTimezone, currentTimezoneFormatInUTC } from '@/views/apps/fortune-wheel/composables/useFormatDate.js'
import { getWheelSpinStatusColor } from '@/views/apps/fortune-wheel/composables/useGetWheelSpinStatusColor.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    wheelType: {
      type: String,
      default: '',
    },
  },
  components: {
    UniversalSelect,
    SearchField,
    CopyValue,
  },
  data: () => ({
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      current: undefined,
      pageSize: undefined,
      sector_type: undefined,
      transaction_status: undefined,
      startDate: undefined,
      endDate: undefined,
      search: '',
      sort_by: undefined,
      sort_direction: undefined,
    },
    columns: [
      {
        title: 'Date',
        dataIndex: 'created_at',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'created_at' },
      },
      {
        title: 'Login',
        dataIndex: 'login_id',
        width: '15%',
        scopedSlots: { customRender: 'login_id' },
      },
      {
        title: 'Sector ID',
        dataIndex: 'sector_id',
        width: '15%',
        scopedSlots: { customRender: 'sector_id' },
      },
      {
        title: 'Sector type',
        dataIndex: 'sector_type',
        width: '15%',
        scopedSlots: { customRender: 'sector_type' },
      },
      {
        title: 'Value',
        dataIndex: 'value',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'value' },
        sorter: true,
        sortOrder: null,
      },
      {
        title: 'Status',
        dataIndex: 'transaction_status',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'transaction_status' },
      },
    ],
    sectorTypeOptions: [],
    statusOptions: [
      { name: 'Success', value: 'success' },
      { name: 'Pending', value: 'pending' },
      { name: 'Failed', value: 'failed' },
    ],
    loading: false,
  }),
  computed: {
    tableColumns() {
      return this.columns.map(col => {
        if (col.dataIndex === 'login_id' && this.wheelType === 'unauthorized') {
          col.title = 'Install ID'
          col.width = '20%'
          col.dataIndex = 'install_id'
          col.scopedSlots = { customRender: 'install_id' }
        }
        if (col.dataIndex === 'login_id' && this.wheelType === 'external') {
          col.title = 'User ID'
        }

        if (col.dataIndex === this.filters.sort_by) {
          return {
            ...col,
            sortOrder: this.filters.sort_direction === 'asc' ? 'ascend' : 'descend',
          }
        }
        return { ...col, sortOrder: null }
      })
    },
    wheelId() {
      return this.$route.params.id
    },
    isHasListItems() {
      return !!this.data?.length
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
    isFiltered() {
      return this.filters.current ||
        this.filters.pageSize ||
        !!this.filters.sector_type ||
        !!this.filters.transaction_status ||
        !!this.filters.startDate ||
        !!this.filters.endDate ||
        !!this.filters.search?.length ||
        this.filters.sort_by ||
        this.filters.sort_direction
    },
  },
  methods: {
    customRow(spin) {
      return {
        on: {
          click: () => {
            this.$router.push(`/fortune-wheel/${this.wheelId}/view-sector/${spin.sector_id}`)
          },
        },
      }
    },
    getPopupContainer() {
      return document.body
    },
    getTypeName(type) {
      return getSectorTypeName(type)
    },
    format(date) {
      return UTCFormatInCurrentTimezone(date, 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY HH:mm')
    },
    copy(value, message) {
      copyToClipboard(value, message)
    },
    getBadgeStatusStyle(status) {
      return getWheelSpinStatusColor(status)
    },
    changeSearchValue(value) {
      this.filters.search = value
      this.changeFilters()
    },
    changeFilters() {
      this.filters.current = undefined
      this.getSpinLogs()
    },
    changePagination(pagination, _, sorter) {
      this.filters.current = pagination.current
      this.filters.pageSize = pagination.pageSize
      if (sorter && sorter?.order) {
        this.filters.sort_by = sorter.field
        this.filters.sort_direction = this.getSortDirectionParam(sorter.order)
      } else {
        this.filters.sort_by = undefined
        this.filters.sort_direction = undefined
      }
      this.getSpinLogs()
    },
    getSortDirectionParam(order) {
      if (order === 'ascend') {
        return 'asc'
      }
      return 'desc'
    },
    getFilters() {
      const filters = {}
      setQueryParams(this.filters, this)

      if (!this.isFiltered) {
        return {}
      }

      if (this.filters.current) {
        filters.page = this.filters.current
      }
      if (this.filters.pageSize) {
        filters.per_page = this.filters.pageSize
      }
      if (this.filters.sort_by) {
        filters.sort_by = this.filters.sort_by
      }
      if (this.filters.sort_direction) {
        filters.sort_direction = this.filters.sort_direction
      }
      if (this.filters.sector_type) {
        filters.sector_type = this.filters.sector_type
      }
      if (this.filters.transaction_status) {
        filters.transaction_status = this.filters.transaction_status
      }
      if (this.filters.startDate) {
        filters.date_from = this.filters.startDate
          ? this.$moment.isMoment(this.filters.startDate)
            ? this.filters.startDate.format('YYYY-MM-DD HH:mm')
            : this.filters.startDate
          : undefined
      }
      if (this.filters.endDate) {
        filters.date_to = this.filters.endDate
          ? this.$moment.isMoment(this.filters.endDate)
            ? this.filters.endDate.format('YYYY-MM-DD HH:mm')
            : this.filters.endDate
          : undefined
      }
      if (this.filters.search?.length) {
        filters.search = this.filters.search
      }

      return filters
    },
    async getSpinLogs() {
      try {
        this.loading = true

        const filters = this.getFilters()
        if (filters.date_from) {
          filters.date_from = currentTimezoneFormatInUTC(filters.date_from)
        }
        if (filters.date_to) {
          filters.date_to = currentTimezoneFormatInUTC(filters.date_to)
        }

        const url = `${wheelApiUrl}/admin/logs/${this.wheelId}`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.$refs['search-field'].clearSearchValue(true)
      this.columns = this.columns.map(col => ({ ...col, sortOrder: null }))
      this.getSpinLogs()
    },
  },
  async mounted() {
    setFilters(this.$route.query, this)
    this.getSpinLogs()
    const sectorTypeOptions = await getSectorTypeOptions(this.$route.params.id)
    this.sectorTypeOptions = sectorTypeOptions.map(option => {
      return {
        name: getSectorTypeName(option),
        value: option,
      }
    })
  },
}
</script>

<style lang="scss">
.spin-history {
  .all-filters {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    margin-bottom: 10px;
  }
  .filters {
    display: flex;
    gap: 10px;
    width: fit-content;
    &__container {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }
  .ant-table-row {
    cursor: pointer;
  }
}
</style>
